<template>
    <div class="ui-page">
        <ui-page-header title="Roteiros de visita"
            subtitle="Cada roteiro guarda a base, as equipes e os casos. No editor você monta a rota no mapa, passa visitas entre dias e equipes, trava os dias combinados e gera as agendas.">
            <v-btn outlined to="/prospeccao/sedes"><v-icon left small>mdi-office-building-marker-outline</v-icon>Sedes e aeroportos</v-btn>
            <v-btn v-if="$allowed('create')" color="primary" depressed @click="openCreate"><v-icon left small>mdi-plus</v-icon>Novo roteiro</v-btn>
        </ui-page-header>

        <ui-section title="Roteiros sugeridos" class="mb-6"
            :description="suggest.result ? `Grupos de casos pendentes por região, com o melhor jeito de chegar a partir da sede. Avião entra quando a estrada passa de ${suggest.result.threshold_hours}h e o voo chega antes. Casos já em roteiro aberto ficam de fora.` : 'Grupos de casos pendentes por região, com o melhor jeito de chegar a partir da sede.'">
            <template #actions>
                <v-select v-model="suggest.office_id" :items="officeItems" label="Sede" dense outlined hide-details :menu-props="{ offsetY: true }" style="max-width: 200px;" @change="loadSuggestions"></v-select>
                <v-select v-model="suggest.uf" :items="ufs" label="UF" dense outlined hide-details clearable :menu-props="{ offsetY: true }" style="max-width: 110px;" @change="loadSuggestions"></v-select>
            </template>
            <div v-if="suggest.loading" class="pt-loading"><v-progress-circular indeterminate size="24" width="3" color="primary"></v-progress-circular></div>
            <ui-empty v-else-if="!suggest.result || !suggest.result.suggestions.length" text="Nenhum caso pendente livre para sugerir. Casos que já estão num roteiro aberto não entram." />
            <div v-else class="pt-suggestions">
                <prospect-trip-suggestion v-for="item in suggest.result.suggestions" :key="item.key" :suggestion="item" :loading="suggest.creating === item.key" @use="createFromSuggestion" />
            </div>
        </ui-section>

        <ui-table-card>
            <template #toolbar>
                <ui-pills v-model="status" :items="statusItems" @input="load" />
                <v-text-field v-model="q" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify" label="Buscar pelo nome" style="max-width: 260px;"
                    @input="scheduleLoad"></v-text-field>
            </template>
            <v-data-table :headers="headers" :items="trips" :loading="loading" disable-sort hide-default-footer :items-per-page="-1"
                no-data-text="Nenhum roteiro ainda. Crie o primeiro em “Novo roteiro”." loading-text="Carregando..." class="ui-clickable-rows" @click:row="open">
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div class="ui-cell-sub">{{ item.base ? `Base ${item.base.name}/${item.base.uf}` : "Sem cidade-base" }}<span v-if="item.start_date"> · saída {{ formatFeedDate(item.start_date) }}</span></div>
                </template>
                <template v-slot:[`item.teams`]="{ item }">
                    <div class="pt-teams">
                        <div v-for="team in item.teams" :key="team.id">
                            <span class="pt-team"><i class="pt-team__dot" :style="{ background: team.color }"></i>{{ team.name }}</span>
                            <div class="ui-cell-sub">{{ team.members.length ? team.members.map((person) => person.name).join(", ") : "sem responsáveis" }}</div>
                        </div>
                    </div>
                </template>
                <template v-slot:[`item.summary`]="{ item }">
                    <span class="ui-nowrap">{{ plural(item.cases, "caso", "casos") }}<span v-if="item.summary"> · {{ item.summary.estimated ? "≈ " : "" }}{{ Number(item.summary.km || 0).toLocaleString("pt-BR") }} km · {{ plural(item.summary.days, "dia", "dias") }}</span></span>
                    <div class="ui-cell-sub">
                        <span v-if="item.meetings">{{ plural(item.meetings, "agenda gerada", "agendas geradas") }}</span><span v-else>sem agendas</span>
                        <span v-if="item.summary && item.summary.warnings" class="pt-warn"> · {{ plural(item.summary.warnings, "aviso", "avisos") }}</span>
                    </div>
                </template>
                <template v-slot:[`item.status`]="{ item }"><ui-badge set="prospect-trip" :status="item.status" /></template>
                <template v-slot:[`item.updated_at`]="{ item }"><span class="ui-nowrap ui-muted">{{ formatDateTime(item.updated_at) }}</span></template>
                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Abrir', icon: 'mdi-pencil-outline', handler: () => open(item) },
                        { text: 'Duplicar', icon: 'mdi-content-copy', handler: () => duplicate(item) },
                        { text: 'Excluir', icon: 'mdi-delete-outline', danger: true, handler: () => remove(item) , disabled: !$allowed('delete') },
                    ]" />
                </template>
            </v-data-table>
        </ui-table-card>

        <v-dialog v-model="create.open" max-width="520" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Novo roteiro</h3>
                        <p class="ui-section-description">Escolha a UF dos casos e a cidade de saída. Os casos e as equipes você define no editor.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-text-field v-model="create.name" label="Nome (opcional)" placeholder="Ex.: Goiás · 2ª quinzena de outubro" outlined dense hide-details="auto" class="mb-3"></v-text-field>
                    <div class="d-flex" style="gap: 12px;">
                        <v-select v-model="create.uf" :items="ufs" label="UF" outlined dense hide-details clearable :menu-props="{ offsetY: true }" style="max-width: 120px;"></v-select>
                        <v-autocomplete v-model="create.base_id" :items="create.baseOptions" item-value="id" :item-text="cityText" :search-input.sync="create.search"
                            label="Cidade-base (saída e volta)" outlined dense hide-details no-filter no-data-text="Digite o nome da cidade"></v-autocomplete>
                    </div>
                    <p class="ui-small ui-muted mt-3 mb-0">Sem cidade-base, o editor sugere a capital da UF com mais casos.</p>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="create.saving" @click="create.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed :loading="create.saving" @click="submitCreate">Criar e abrir</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import ProspectTripSuggestion from "@/components/prospect/ProspectTripSuggestion.vue";
import { formatDateTime, formatFeedDate, notifyError, notifySuccess, TRIP_STATUS_ITEMS, UF_ITEMS } from "@/components/prospect/prospect.util";

export default {
    name: "ProspectTrips",

    components: { ProspectTripSuggestion },

    data: () => ({
        trips: [],
        loading: false,
        status: "abertos",
        q: "",
        timer: null,
        ufs: UF_ITEMS,
        create: { open: false, saving: false, name: "", uf: null, base_id: null, baseOptions: [], search: "" },
        searchTimer: null,
        offices: [],
        suggest: { office_id: null, uf: null, loading: false, result: null, creating: null },
        headers: [
            { text: "Roteiro", value: "name" },
            { text: "Equipes", value: "teams" },
            { text: "Resumo", value: "summary" },
            { text: "Situação", value: "status", width: 140 },
            { text: "Atualizado", value: "updated_at", width: 150 },
            { text: "", value: "actions", width: 48, align: "end" },
        ],
    }),

    computed: {
        officeItems() {
            return this.offices.map((office) => ({ value: office.id, text: office.name }));
        },

        statusItems() {
            return [{ value: "abertos", text: "Abertos" }, ...TRIP_STATUS_ITEMS, { value: "todos", text: "Todos" }];
        },
    },

    watch: {
        "create.search"(term) {
            clearTimeout(this.searchTimer);
            if (!term || term.length < 2) return;
            this.searchTimer = setTimeout(async () => {
                try {
                    const params = { q: term };
                    if (this.create.uf) params.uf = this.create.uf;
                    const result = await axios.get("/api/admin/prospect/municipalities", { params });
                    const current = this.create.baseOptions.filter((item) => item.id === this.create.base_id);
                    this.create.baseOptions = [...current, ...result.data.content.filter((item) => item.id !== this.create.base_id)];
                } catch (error) {
                    notifyError(this, error);
                }
            }, 300);
        },
    },

    created() {
        this.load();
        this.loadOffices();
    },

    methods: {
        formatDateTime,
        formatFeedDate,

        plural(count, one, many) {
            const value = Number(count) || 0;
            return `${value.toLocaleString("pt-BR")} ${value === 1 ? one : many}`;
        },

        cityText(city) {
            return city ? `${city.name}/${city.uf}` : "";
        },

        scheduleLoad() {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.load(), 300);
        },

        async load() {
            this.loading = true;
            try {
                const params = {};
                if (!["abertos", "todos"].includes(this.status)) params.status = this.status;
                if (this.q) params.q = this.q;
                const result = await axios.get("/api/admin/prospect/trips", { params });
                const open = ["rascunho", "planejado", "em_andamento"];
                this.trips = this.status === "abertos" ? result.data.content.filter((item) => open.includes(item.status)) : result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        async loadOffices() {
            try {
                const result = await axios.get("/api/admin/prospect/offices");
                this.offices = result.data.content.offices;
                const preferred = this.offices.find((office) => office.is_default) || this.offices[0];
                this.suggest.office_id = preferred ? preferred.id : null;
                this.loadSuggestions();
            } catch (error) {
                notifyError(this, error);
            }
        },

        async loadSuggestions() {
            if (!this.suggest.office_id) return;
            this.suggest.loading = true;
            try {
                const params = { office_id: this.suggest.office_id };
                if (this.suggest.uf) params.uf = this.suggest.uf;
                const result = await axios.get("/api/admin/prospect/trips/suggestions", { params });
                this.suggest.result = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.suggest.loading = false;
            }
        },

        // Cria o roteiro com os casos, a sede, o modo e a base sugeridos e abre o editor.
        async createFromSuggestion(item) {
            this.suggest.creating = item.key;
            try {
                const result = await axios.post("/api/admin/prospect/trips", {
                    name: `Roteiro ${item.title}`.slice(0, 150),
                    office_id: this.suggest.office_id,
                    travel_mode: item.travel_mode,
                    arrival_airport_id: item.arrival_airport ? item.arrival_airport.id : undefined,
                    base_id: item.travel_mode === "car" ? item.base.id : undefined,
                    uf: item.ufs.length === 1 ? item.ufs[0] : undefined,
                    case_ids: item.cases.map((entry) => entry.id),
                });
                this.$router.push(`/prospeccao/rota/${result.data.content.id}`);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.suggest.creating = null;
            }
        },

        open(item) {
            this.$router.push(`/prospeccao/rota/${item.id}`);
        },

        openCreate() {
            this.create = { open: true, saving: false, name: "", uf: null, base_id: null, baseOptions: [], search: "" };
        },

        async submitCreate() {
            this.create.saving = true;
            try {
                const result = await axios.post("/api/admin/prospect/trips", { name: this.create.name || undefined, uf: this.create.uf || undefined, base_id: this.create.base_id || undefined });
                this.create.open = false;
                this.$router.push(`/prospeccao/rota/${result.data.content.id}`);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.create.saving = false;
            }
        },

        async duplicate(item) {
            try {
                const result = await axios.post(`/api/admin/prospect/trips/${item.id}/duplicate`);
                notifySuccess(this, "Roteiro duplicado como rascunho.");
                this.$router.push(`/prospeccao/rota/${result.data.content.id}`);
            } catch (error) {
                notifyError(this, error);
            }
        },

        async remove(item) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir roteiro",
                text: `"${item.name}" sai da lista. ${item.meetings ? "As agendas já geradas continuam marcadas; cancele em Agendas se for o caso." : ""}`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`/api/admin/prospect/trips/${item.id}`);
                notifySuccess(this, "Roteiro excluído.");
                this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pt-teams {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 160px;
    font-size: 13px;
}

.pt-team {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    font-weight: 500;
}

.pt-team__dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
}

.pt-loading {
    display: flex;
    justify-content: center;
    padding: 24px 0;
}

.pt-suggestions {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 12px;
}

.pt-warn {
    color: #b45309;
}
</style>
